import { APP_CONSTANTS } from "@/config";
import { useAuthStore } from "@/stores/use-auth-store";

function unverifiedSession(): Error {
  return new Error("Sua sessão precisa ser verificada antes de continuar.");
}

const operations = new WeakSet<object>();

export interface CopilotOperation {
  readonly headers: Readonly<Record<string, string>>;
  isCurrent: () => boolean;
  assertCurrent: () => void;
  fetch: typeof fetch;
  guardStream: <T>(source: ReadableStream<T>) => ReadableStream<T>;
}

/** Capture before any preparation/await. A capsule is never rebound to a new actor. */
export function captureCopilotOperation(): CopilotOperation {
  const session = useAuthStore.getState();
  const token = localStorage.getItem(APP_CONSTANTS.AUTH_ACCESS_TOKEN_LOCAL_STORAGE_KEY);
  const tenant = localStorage.getItem(APP_CONSTANTS.COMPANY_LOCAL_STORAGE_KEY);
  const isCurrent = () => {
    const current = useAuthStore.getState();
    return Boolean(token && tenant) && current.sessionStatus === "ready" && !current.isDisconnected
      && current.sessionRevision === session.sessionRevision && current.user?.id === session.user?.id
      && current.accessToken === token
      && localStorage.getItem(APP_CONSTANTS.AUTH_ACCESS_TOKEN_LOCAL_STORAGE_KEY) === token
      && localStorage.getItem(APP_CONSTANTS.COMPANY_LOCAL_STORAGE_KEY) === tenant;
  };
  const assertCurrent = () => { if (!isCurrent()) throw unverifiedSession(); };
  assertCurrent();

  const observe = (invalidate: () => void) => {
    const check = () => { if (!isCurrent()) invalidate(); };
    const unsubscribe = useAuthStore.subscribe(check);
    if (typeof window !== "undefined") window.addEventListener("storage", check);
    return () => {
      unsubscribe();
      if (typeof window !== "undefined") window.removeEventListener("storage", check);
    };
  };

  function guardStream<T>(source: ReadableStream<T>, onFinish: () => void = () => {}): ReadableStream<T> {
    const reader = source.getReader();
    let finished = false;
    let cleanup = () => {};
    const dispose = () => { cleanup(); onFinish(); };
    return new ReadableStream<T>({
      start(controller) {
        cleanup = observe(() => {
          if (finished) return;
          finished = true;
          dispose();
          const error = unverifiedSession();
          controller.error(error);
          void reader.cancel(error).catch(() => {});
        });
      },
      async pull(controller) {
        try {
          assertCurrent();
          const chunk = await reader.read();
          assertCurrent();
          if (finished) return;
          if (chunk.done) {
            finished = true;
            dispose();
            reader.releaseLock();
            controller.close();
          } else controller.enqueue(chunk.value);
        } catch (error) {
          if (finished) return;
          finished = true;
          dispose();
          controller.error(error);
          void reader.cancel(error).catch(() => {});
        }
      },
      cancel(reason) {
        finished = true;
        dispose();
        void reader.cancel(reason).catch(() => {});
      },
    }, { highWaterMark: 0 });
  }

  function guardResponse(original: Response, onFinish: () => void = () => {}): Response {
    const response = new Response(original.body ? guardStream(original.body, onFinish) : null, {
      status: original.status, statusText: original.statusText, headers: original.headers,
    });
    for (const key of ["url", "redirected", "type"] as const) {
      Object.defineProperty(response, key, { value: original[key] });
    }
    // A stream may finish before JSON decoding/blob construction settles. Check
    // both ends of every body consumer, including clones and buffered responses.
    for (const method of ["json", "text", "blob", "arrayBuffer", "formData", "bytes"] as const) {
      const consume = Reflect.get(response, method) as (() => Promise<unknown>) | undefined;
      if (!consume) continue;
      Object.defineProperty(response, method, { value: async () => {
        assertCurrent();
        const value = await consume.call(response);
        assertCurrent();
        return value;
      } });
    }
    const clone = response.clone.bind(response);
    Object.defineProperty(response, "clone", { value: () => {
      assertCurrent();
      return guardResponse(clone());
    } });
    return response;
  }

  const boundFetch: typeof fetch = async (input, init) => {
    assertCurrent();
    const headers = new Headers(init?.headers ?? (input instanceof Request ? input.headers : undefined));
    if (headers.get("Authorization") !== `Bearer ${token}` || headers.get("X-Tenant") !== tenant) {
      throw unverifiedSession();
    }
    const abort = new AbortController();
    const signal = init?.signal ?? (input instanceof Request ? input.signal : undefined);
    const onAbort = () => abort.abort(signal?.reason);
    const cleanup = observe(() => abort.abort(unverifiedSession()));
    signal?.addEventListener("abort", onAbort, { once: true });
    if (signal?.aborted) onAbort();
    const release = () => { cleanup(); signal?.removeEventListener("abort", onAbort); };
    let response: Response | undefined;
    let reading = false;
    try {
      response = await fetch(input, { ...init, signal: abort.signal });
      assertCurrent();
      if (abort.signal.aborted) throw abort.signal.reason;
      const guarded = guardResponse(response, release);
      reading = Boolean(response.body);
      return guarded;
    } catch (error) {
      if (response?.body) void response.body.cancel().catch(() => {});
      throw error;
    } finally {
      if (!reading) release();
    }
  };

  const operation = Object.freeze({
    headers: Object.freeze({ Authorization: `Bearer ${token}`, "X-Tenant": tenant! }),
    isCurrent, assertCurrent, fetch: boundFetch, guardStream,
  });
  operations.add(operation);
  return operation;
}

/** Internal SDK metadata carries the already-captured user action, never the wire body. */
export function operationFor(metadata: unknown): CopilotOperation {
  return typeof metadata === "object" && metadata !== null && operations.has(metadata)
    ? metadata as CopilotOperation : captureCopilotOperation();
}

/** Direct REST/audio calls capture synchronously; SDK calls bind before SDK awaits. */
export const fetchCopilotBff: typeof fetch = async (input, init) => captureCopilotOperation().fetch(input, init);
